// session.js — startup from the local mirror, and the per-user load/merge that
// runs whenever Firebase reports a signed-in user (anonymous ones included).

import { S, ensureIds, normalizeLists, mergeInto, buildState, realId } from './state.js';
import { saveToLocal, loadLocal, loadStreakLocal, saveStreakLocal } from './local.js';
import { loadUserData, saveUserData, getCloudTotals, trimCompletedToWindow, historyWindowStart, HISTORY_WINDOW_DAYS } from '../db.js';
import { normalizeTask, normalizeCompleted } from '../model.js';
import { restoreActiveTask, updateLockState } from './taskbox.js';
import { renderTagList } from './tags.js';
import { updateStats, renderStreakCount } from './statsbox.js';
import { closeShowAll, isShowAllOpen } from './showall.js';

// Before the cloud answers: show what this device has.
export function bootstrapLocal() {
  loadStreakLocal();
  renderStreakCount();
  loadLocal();
  // Migration: give pre-existing records ids, and write them back so the
  // login merge (which re-reads localStorage) sees the same ids.
  normalizeLists();
  saveToLocal();
  renderTagList();
  updateLockState();
  restoreActiveTask(null);
  updateStats(); // stats box reflects local data immediately, not only after the first action
}

// Loads the cloud data for this uid and merges this device's local data into it.
export async function onUserLogin(user) {
  console.log(user.isAnonymous ? "Anonymous session:" : "User logged in:", user.uid);
  if (isShowAllOpen()) closeShowAll(); // the list would be stale after an account change

  // Step 1: this device's data. Records without an id (an old local copy) get
  // provisional ids so they are matched to the cloud by text, never uploaded as new.
  const localTasks = ensureIds(JSON.parse(localStorage.getItem('fishbowlTasks') || '[]'), true).map(t => normalizeTask(t)).filter(Boolean);
  const localCompleted = ensureIds(JSON.parse(localStorage.getItem('fishbowlDone') || '[]'), true).map(c => normalizeCompleted(c)).filter(Boolean);
  const localTags = JSON.parse(localStorage.getItem('fishTags') || '[]');
  const localManualQueue = ensureIds(JSON.parse(localStorage.getItem('manualQueue') || '[]'), true).map(q => normalizeTask(q)).filter(Boolean);

  // Step 2: the cloud's. Normally only the recent history window is loaded, but
  // a device whose local copy holds older completed records (a phone that was
  // not used for months) needs the full history so those can be matched by
  // text; otherwise they would be re-uploaded and counted again (2026-09-18).
  const windowStart = historyWindowStart();
  const staleMirror = localCompleted.some(c => (c.completedOn || '') < windowStart);
  const dbData = await loadUserData(staleMirror ? { all: true } : {});

  if (dbData?.streakInfo) {
    S.streak = dbData.streakInfo.streak || 0;
    S.lastCatchDate = dbData.streakInfo.lastCatchDate || '';
    saveStreakLocal();
    renderStreakCount();
  }

  if (!dbData) {
    // New account — this device's data becomes the cloud's
    S.tasks = localTasks;
    S.completed = localCompleted;
    S.tags = localTags;
    S.manualQueue = localManualQueue;
    console.log('Initialized Firestore with local data.');
  } else {
    // Existing account — merge this device's additions into the cloud data.
    const cloudCompleted = dbData.completed || [];
    S.deletedIds = [...new Set([...(dbData.deletedIds || []), ...S.deletedIds])].slice(-200);
    const gone = new Set(S.deletedIds);
    // A local record that the cloud already lists as completed, or that was
    // deleted on another device, must not be resurrected from this device's mirror.
    // A record with a provisional/legacy id can only be recognised by text.
    const completedElsewhere = t => gone.has(t.id) || cloudCompleted.some(c => c.id === t.id || (!realId(t.id) && c.text === t.text));
    // Content identity for completed records: two records with the same text,
    // completion time and duration are the same event whatever their ids. A
    // device holding an old copy under different ids therefore cannot upload
    // history the cloud already has (2026-09-18 duplication incident).
    const contentKey = c => `${c.text}|${c.completedOn || ''}|${c.durationSec == null ? '' : c.durationSec}`;
    // Only cloud records with a real id take part: a legacy-id cloud record
    // still needs the local copy's id lent to it by mergeInto (schema-1 migration).
    const cloudContent = new Set(cloudCompleted.filter(c => realId(c.id)).map(contentKey));
    const deletedElsewhere = c => gone.has(c.id) || (!cloudCompleted.some(x => x.id === c.id) && cloudContent.has(contentKey(c)));

    S.tasks = ensureIds(mergeInto(dbData.tasks || [], localTasks, completedElsewhere));
    // Local completed records older than the loaded history window are kept:
    // saveUserData checks the cloud for each before uploading or counting it.
    S.completed = ensureIds(mergeInto(cloudCompleted, localCompleted, deletedElsewhere));
    S.manualQueue = ensureIds(mergeInto(dbData.manualQueue || [], localManualQueue, completedElsewhere));
    S.tags = [...new Set([...(dbData.tags || []), ...localTags])];
    console.log('Synced local additions into Firestore.');
  }

  restoreActiveTask(dbData?.active || null);
  await saveUserData(buildState());
  S.completed = trimCompletedToWindow(S.completed); // older records are in the cloud; keep memory to the window
  S.historyWindowDays = HISTORY_WINDOW_DAYS; // in-memory history is trimmed to the window after every login
  S.cloudTotals = getCloudTotals() || (dbData?.totals ? { ...dbData.totals } : null);

  renderTagList();
  updateLockState();
  saveToLocal(); // refresh the mirror with merged data
  updateStats();

  if (!user.isAnonymous) document.getElementById('signup-reminder')?.classList.add('hidden');
}
